import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { ElementType, HTMLAttributes, ReactNode, useMemo } from "react";
import { cx } from "../../utils/cx";
import { Link } from "../Link";
import linkStyles from "../Link/Link.module.scss";
import styles from "./Breadcrumbs.module.scss";

export type BreadcrumbItem = {
    /** Stable key for React lists and menu items. */
    id: string;
    label: ReactNode;
    /**
     * Full URL or in-app path for DS `<Link href>` (document navigation).
     * When both `href` and `to` are set, `href` wins.
     */
    href?: string;
    /**
     * In-app route for SPAs. Uses `routerLinkAs` (e.g. React Router `Link`) when set.
     * Ignored for the last item (current page).
     */
    to?: string;
};

export type BreadcrumbsProps = Omit<HTMLAttributes<HTMLElement>, "children"> & {
    items: BreadcrumbItem[];
    /**
     * When `items.length` is at least this value, middle segments collapse into
     * the ellipsis menu (Figma `4+`). Default `4`.
     */
    collapseThreshold?: number;
    /**
     * Text scale. `md` = Figma trail (14). `lg` = page-title trail (24) used
     * by the app sub-header to match legacy Settings disposition.
     */
    size?: "md" | "lg";
    /** `nav` label for assistive tech. */
    "aria-label"?: string;
    /** `aria-label` on the ellipsis trigger. */
    ellipsisLabel?: string;
    /**
     * Renders segments with `to` (and no `href`) for client-side navigation.
     * Example: `import { Link } from "react-router-dom"` → `routerLinkAs={Link}`.
     * If omitted, `to` falls back to DS `<Link href={to}>`.
     */
    routerLinkAs?: ElementType<{
        to: string;
        className?: string;
        children?: ReactNode;
    }>;
};

function ChevronRight() {
    return (
        <svg aria-hidden fill="none" height={18} viewBox="0 0 18 18" width={18}>
            <path
                d="M6.75 4.5 11.25 9l-4.5 4.5"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
            />
        </svg>
    );
}

function MoreHorizontal() {
    return (
        <svg
            aria-hidden
            fill="currentColor"
            height={18}
            viewBox="0 0 18 18"
            width={18}
        >
            <circle cx={3.5} cy={9} r={1.5} />
            <circle cx={9} cy={9} r={1.5} />
            <circle cx={14.5} cy={9} r={1.5} />
        </svg>
    );
}

function Separator() {
    return (
        <li aria-hidden className={styles.separator}>
            <ChevronRight />
        </li>
    );
}

/**
 * Treez DS — Breadcrumbs
 *
 * Hierarchy trail (Figma `6766:45416`). Last item is the current page
 * (`aria-current="page"`). When there are many levels, middle items move into
 * an ellipsis flyout (`collapseThreshold`, default matches Figma `4+`).
 *
 * For React Router (or similar), pass `items` with `to` on segments and
 * `routerLinkAs={Link}` from `react-router-dom` so navigation stays in-app.
 */
const linkVisualPropsFor = (size: "md" | "lg") => ({
    "data-size": size === "lg" ? ("lg" as const) : ("md" as const),
    "data-ui": "link" as const,
    "data-variant": "secondary" as const,
    "data-underline": "hover" as const,
});

export function Breadcrumbs({
    items,
    collapseThreshold = 4,
    size = "md",
    className,
    "aria-label": ariaLabel = "Breadcrumb",
    ellipsisLabel = "Show more breadcrumbs",
    routerLinkAs,
    ...rest
}: BreadcrumbsProps) {
    const collapsed = items.length > 1 && items.length >= collapseThreshold;
    const linkVisualProps = linkVisualPropsFor(size);

    const { head, middle, tail } = useMemo(() => {
        if (!collapsed) {
            return {
                head: [] as BreadcrumbItem[],
                middle: [] as BreadcrumbItem[],
                tail: [] as BreadcrumbItem[],
            };
        }
        return {
            head: items.slice(0, 1),
            middle: items.slice(1, -1),
            tail: items.slice(-1),
        };
    }, [collapsed, items]);

    if (!items.length) {
        return null;
    }

    const renderLink = (item: BreadcrumbItem, isCurrent: boolean) => {
        if (isCurrent) {
            return (
                <span aria-current="page" className={styles.current}>
                    {item.label}
                </span>
            );
        }
        if (item.href) {
            return (
                <Link href={item.href} size={size === "lg" ? "lg" : "md"}>
                    {item.label}
                </Link>
            );
        }
        if (item.to) {
            if (routerLinkAs) {
                const R = routerLinkAs;
                return (
                    <R
                        className={cx(linkStyles.root)}
                        to={item.to}
                        {...linkVisualProps}
                    >
                        <span className={linkStyles.label}>{item.label}</span>
                    </R>
                );
            }
            return (
                <Link href={item.to} size={size === "lg" ? "lg" : "md"}>
                    {item.label}
                </Link>
            );
        }
        return <span className={styles.static}>{item.label}</span>;
    };

    const renderTrail = () => {
        const nodes: ReactNode[] = [];

        const pushCrumb = (item: BreadcrumbItem, isCurrent: boolean) => {
            nodes.push(
                <li className={styles.crumb} key={item.id}>
                    {renderLink(item, isCurrent)}
                </li>,
            );
        };

        if (!collapsed) {
            items.forEach((item, index) => {
                if (index > 0) {
                    nodes.push(<Separator key={`sep-${item.id}`} />);
                }
                pushCrumb(item, index === items.length - 1);
            });
            return nodes;
        }

        head.forEach((item, index) => {
            if (index > 0) {
                nodes.push(<Separator key={`sep-h-${item.id}`} />);
            }
            pushCrumb(item, false);
        });

        if (middle.length > 0) {
            nodes.push(<Separator key="sep-ellipsis" />);
            nodes.push(
                <li className={styles.crumb} key="ellipsis">
                    <DropdownMenu.Root>
                        <DropdownMenu.Trigger asChild>
                            <button
                                aria-haspopup="menu"
                                aria-label={ellipsisLabel}
                                className={styles.ellipsisTrigger}
                                type="button"
                            >
                                <MoreHorizontal />
                            </button>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Portal>
                            <DropdownMenu.Content
                                align="start"
                                className={styles.menuContent}
                                loop
                                side="bottom"
                                sideOffset={6}
                            >
                                {middle.map(item => {
                                    if (item.href) {
                                        return (
                                            <DropdownMenu.Item
                                                asChild
                                                className={styles.menuItem}
                                                key={item.id}
                                            >
                                                <a href={item.href}>
                                                    <span
                                                        className={
                                                            styles.menuItemLabel
                                                        }
                                                    >
                                                        {item.label}
                                                    </span>
                                                </a>
                                            </DropdownMenu.Item>
                                        );
                                    }
                                    if (item.to && routerLinkAs) {
                                        const R = routerLinkAs;
                                        return (
                                            <DropdownMenu.Item
                                                asChild
                                                className={styles.menuItem}
                                                key={item.id}
                                            >
                                                <R to={item.to}>
                                                    <span
                                                        className={
                                                            styles.menuItemLabel
                                                        }
                                                    >
                                                        {item.label}
                                                    </span>
                                                </R>
                                            </DropdownMenu.Item>
                                        );
                                    }
                                    return (
                                        <DropdownMenu.Item
                                            className={styles.menuItem}
                                            disabled
                                            key={item.id}
                                        >
                                            <span
                                                className={styles.menuItemLabel}
                                            >
                                                {item.label}
                                            </span>
                                        </DropdownMenu.Item>
                                    );
                                })}
                            </DropdownMenu.Content>
                        </DropdownMenu.Portal>
                    </DropdownMenu.Root>
                </li>,
            );
        }

        tail.forEach(item => {
            nodes.push(<Separator key={`sep-t-${item.id}`} />);
            pushCrumb(item, true);
        });

        return nodes;
    };

    return (
        <nav
            aria-label={ariaLabel}
            className={cx(styles.nav, className)}
            data-size={size}
            data-ui="breadcrumbs"
            {...rest}
        >
            <ol className={styles.list}>{renderTrail()}</ol>
        </nav>
    );
}

Breadcrumbs.displayName = "Breadcrumbs";
